import React, { useEffect, useState, useMemo } from 'react';
import { fetchWithAuth } from './lib/api';
import { 
  Typography, TextField, Button, MenuItem, Select, InputLabel, FormControl, Box, 
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, FormGroup,
  FormControlLabel, Checkbox, Chip, Paper, Divider, Alert,
  Tabs, Tab, Radio, RadioGroup, CircularProgress, Collapse,
  useMediaQuery, useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { usePositions } from './PositionsContext';
import { usePresetHours } from './PresetHoursContext';
import { useEmployees, useCreateEmployee, useUpdateEmployee, useDeleteEmployee, useToggleEmployeeActive } from './hooks/useEmployees';
import { useEmployeePayments, useCumulativePayments, useCreatePayment, useUpdatePayment, useDeletePayment } from './hooks/useEmployeePayments';
import { useShifts } from './hooks/useShifts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './utils/permissions';
import { AccessDenied } from './components/PermissionGate';
import EmployeeManagementModal from './components/EmployeeManagementModal';
import PlanLimitModal from './components/PlanLimitModal';
import { useBilling } from './hooks/useBilling';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const DAYS_OF_WEEK = [
  { value: 1, label: 'Δευτέρα' },
  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },
  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },
  { value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' }
];

const SHIFT_PREFERENCES = [
  { value: 'any', label: 'Όλες οι βάρδιες' },
  { value: 'morning', label: 'Προτιμά πρωινές' },
  { value: 'afternoon', label: 'Προτιμά απογευματινές' },
  { value: 'evening', label: 'Προτιμά βραδινές' },
  { value: 'no_night', label: 'Αποφεύγει νυχτερινές' }
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function Staff() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { positions, shifts } = usePositions();
  const { presetHours } = usePresetHours();
  
  // Permission checks
  const { can } = usePermissions();
  const canView = can(PERMISSIONS.EMPLOYEES_VIEW);
  const canCreate = can(PERMISSIONS.EMPLOYEES_CREATE);
  const canEdit = can(PERMISSIONS.EMPLOYEES_EDIT);
  const canDelete = can(PERMISSIONS.EMPLOYEES_DELETE);
  const canManageShifts = can(PERMISSIONS.SHIFTS_CREATE);
  const {
    canAddEmployee,
    maxActiveEmployees,
    activeEmployeeCount,
    canStartTrial,
    effectivePlan,
    demoMode,
  } = useBilling();
  const [planLimitModal, setPlanLimitModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for active, 1 for inactive
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState(''); // Search by employee name
  const [addFormExpanded, setAddFormExpanded] = useState(!isMobile); // Collapsed on mobile by default
  const [form, setForm] = useState({
    name: '',
    email: '',
    position: positions[0] || '',
    shift_preference: shifts[0] || '',
    hourly_wage: '',
    max_days_per_week: 7,
    available_days: '0,1,2,3,4,5,6',
    shift_preferences: 'any',
    notes: ''
  });
  
  const [editModal, setEditModal] = useState({ open: false, emp: null });
  const [preferencesModal, setPreferencesModal] = useState({ open: false, emp: null });
  const [quickAddModal, setQuickAddModal] = useState({ open: false, emp: null });
  const [paymentHistoryModal, setPaymentHistoryModal] = useState({ open: false, emp: null });
  
  // Unified employee management modal
  const [unifiedModal, setUnifiedModal] = useState({ open: false, emp: null, tab: 0 });
  const [quickAddForm, setQuickAddForm] = useState({
    selectedDays: [],
    startTime: '',
    endTime: '',
    weekOffset: 0, // 0: current week, 1: next week, 2: week after next
    dayTimes: {} // { dayValue: { startTime: '', endTime: '' } }
  });
  const [editForm, setEditForm] = useState({});
  const [alert, setAlert] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  
  // Fixed Schedule state
  const [fixedSchedule, setFixedSchedule] = useState({
    enabled: false,
    days: {} // { 0: { enabled: false, start_time: '', end_time: '' }, 1: {...}, ... }
  });
  const [savingFixedSchedule, setSavingFixedSchedule] = useState(false);
  const [paymentStats, setPaymentStats] = useState({ totalSalary: 0, totalPaid: 0, unpaid: 0 });
  const [paymentStatsFilter, setPaymentStatsFilter] = useState('current_week'); // 'current_week' or 'all_time'
  const [employeeShifts, setEmployeeShifts] = useState([]); // All shifts for salary calculation
  const [currentEmployee, setCurrentEmployee] = useState(null); // Current employee data
  
  // Filters for payment history modal
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyGroupBy, setHistoryGroupBy] = useState('none'); // 'none', 'day', 'week', 'month'
  
  // console.log('🔵 Staff.js loaded - Filter state:', { historyDateFrom, historyDateTo, historyGroupBy });
  
  // Edit payment state
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({
    payment_date: '',
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  
  // New payment state
  const [newPaymentDialog, setNewPaymentDialog] = useState(false);
  const [newPaymentForm, setNewPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  const [unpaidWeeks, setUnpaidWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);

  const openEditModal = emp => {
    setEditForm({ 
      name: emp.name,
      email: emp.email || '',
      position: emp.position,
      hourly_wage: emp.hourly_wage,
      saturday_wage: emp.saturday_wage || '',
      sunday_wage: emp.sunday_wage || '',
      shift_preference: emp.shift_preference 
    });
    setEditModal({ open: true, emp });
  };

  const openPreferencesModal = emp => {
    setEditForm({
      max_days_per_week: emp.max_days_per_week || 7,
      available_days: emp.available_days || '0,1,2,3,4,5,6',
      shift_preferences: emp.shift_preferences || 'any',
      notes: emp.notes || ''
    });
    setPreferencesModal({ open: true, emp });
  };

  const openQuickAddModal = emp => {
    // Use first preset hours as default, or fallback to 09:00-17:00
    let defaultStart = '09:00';
    let defaultEnd = '17:00';
    
    if (presetHours && presetHours.length > 0) {
      const [start, end] = presetHours[0].split('-');
      defaultStart = start;
      defaultEnd = end;
    }
    
    // Initialize dayTimes with preset hours for all days
    const dayTimes = {};
    DAYS_OF_WEEK.forEach(day => {
      dayTimes[day.value] = {
        startTime: defaultStart,
        endTime: defaultEnd
      };
    });
    
    setQuickAddForm({
      selectedDays: [],
      startTime: defaultStart,
      endTime: defaultEnd,
      weekOffset: 0,
      dayTimes: dayTimes
    });
    setQuickAddModal({ open: true, emp });
  };

  const closeEditModal = () => setEditModal({ open: false, emp: null });
  const closePreferencesModal = () => setPreferencesModal({ open: false, emp: null });
  const closeQuickAddModal = () => setQuickAddModal({ open: false, emp: null });
  const closePaymentHistoryModal = () => {
    setPaymentHistoryModal({ open: false, emp: null });
    // Reset filters
    setHistoryDateFrom('');
    setHistoryDateTo('');
    setHistoryGroupBy('none');
  };
  
  // Filter and group payment history based on filters
  const getFilteredAndGroupedHistory = () => {
    let filtered = [...paymentHistory];
    
    // Apply date filters
    if (historyDateFrom) {
      filtered = filtered.filter(p => dayjs(p.payment_date).isSameOrAfter(dayjs(historyDateFrom), 'day'));
    }
    if (historyDateTo) {
      filtered = filtered.filter(p => dayjs(p.payment_date).isSameOrBefore(dayjs(historyDateTo), 'day'));
    }
    
    // If no grouping, return filtered list
    if (historyGroupBy === 'none') {
      return filtered.map(p => ({
        ...p,
        groupLabel: null,
        items: [p]
      }));
    }
    
    // Group by day, week, or month
    const groups = {};
    filtered.forEach(payment => {
      let groupKey;
      let groupLabel;
      
      if (historyGroupBy === 'day') {
        groupKey = dayjs(payment.payment_date).format('YYYY-MM-DD');
        groupLabel = dayjs(payment.payment_date).format('DD/MM/YYYY (dddd)');
      } else if (historyGroupBy === 'week') {
        const weekStart = dayjs(payment.payment_date).startOf('isoWeek').format('YYYY-MM-DD');
        const weekEnd = dayjs(payment.payment_date).endOf('isoWeek').format('YYYY-MM-DD');
        groupKey = weekStart;
        groupLabel = `Εβδομάδα ${dayjs(weekStart).format('DD/MM/YYYY')} - ${dayjs(weekEnd).format('DD/MM/YYYY')}`;
      } else if (historyGroupBy === 'month') {
        groupKey = dayjs(payment.payment_date).format('YYYY-MM');
        groupLabel = dayjs(payment.payment_date).format('MMMM YYYY');
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          groupKey,
          groupLabel,
          items: [],
          totalAmount: 0
        };
      }
      
      groups[groupKey].items.push(payment);
      groups[groupKey].totalAmount += parseFloat(payment.amount);
    });
    
    // Convert to array and sort by date (newest first)
    return Object.values(groups).sort((a, b) => b.groupKey.localeCompare(a.groupKey));
  };

  // Calculate filtered stats based on date range
  const calculateFilteredStats = () => {
    console.log('🧮 Calculating filtered stats...', { historyDateFrom, historyDateTo, shifts: employeeShifts.length });
    
    // If no filters, use all data
    if (!historyDateFrom && !historyDateTo) {
      return null; // Will use original paymentStats
    }
    
    // Filter shifts by date range
    let filteredShifts = [...employeeShifts];
    
    // Calculate actual date for each shift (week_start + day offset)
    filteredShifts = filteredShifts.map(shift => {
      const day = parseInt(shift.day);
      const actualDate = dayjs(shift.week_start).add(day === 0 ? 6 : day - 1, 'day');
      // console.log(`  📅 Shift day=${shift.day}, week_start=${shift.week_start} → actualDate=${actualDate.format('YYYY-MM-DD')}`);
      return { ...shift, actualDate };
    });
    
    // console.log(`🔍 Date filter: FROM ${historyDateFrom || 'none'} TO ${historyDateTo || 'none'}`);
    
    if (historyDateFrom) {
      const beforeFilter = filteredShifts.length;
      filteredShifts = filteredShifts.filter(s => s.actualDate.isSameOrAfter(dayjs(historyDateFrom), 'day'));
      // console.log(`  ➡️ After FROM filter: ${beforeFilter} → ${filteredShifts.length} shifts`);
    }
    if (historyDateTo) {
      const beforeFilter = filteredShifts.length;
      filteredShifts = filteredShifts.filter(s => s.actualDate.isSameOrBefore(dayjs(historyDateTo), 'day'));
      console.log(`  ➡️ After TO filter: ${beforeFilter} → ${filteredShifts.length} shifts`);
    }
    
    console.log('📅 Filtered shifts:', filteredShifts.length, 'shifts in range');
    
    // Calculate salary from filtered shifts
    let totalSalary = 0;
    filteredShifts.forEach(shift => {
      // Calculate hours from start_time and end_time
      const startParts = shift.start_time.split(':');
      const endParts = shift.end_time.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      // Handle shifts that cross midnight (e.g., 16:30-00:30)
      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }
      const hours = (endMinutes - startMinutes) / 60;
      
      if (hours > 0 && currentEmployee) {
        // USE STORED WAGE FROM SHIFT FOR HISTORICAL ACCURACY
        // Fallback to employee wage for older shifts that don't have hourly_wage stored
        let wageRate = shift.hourly_wage;
        
        if (!wageRate) {
          // For old shifts without stored wage, determine based on day
          const empData = shift.employee || currentEmployee;
          if (empData) {
            // day: 0=Sunday, 6=Saturday
            if (shift.day === 0) {
              wageRate = empData.sunday_wage || empData.hourly_wage || 0;
            } else if (shift.day === 6) {
              wageRate = empData.saturday_wage || empData.hourly_wage || 0;
            } else {
              wageRate = empData.hourly_wage || 0;
            }
          } else {
            wageRate = 0;
          }
        }

        const shiftSalary = hours * wageRate;
        console.log(`  Shift ${shift.actualDate.format('DD/MM')}: ${hours}h × €${wageRate} = €${shiftSalary.toFixed(2)}`);
        totalSalary += shiftSalary;
      }
    });
    
    // Filter payments by date range
    let filteredPayments = [...paymentHistory];
    if (historyDateFrom) {
      filteredPayments = filteredPayments.filter(p => dayjs(p.payment_date).isSameOrAfter(dayjs(historyDateFrom), 'day'));
    }
    if (historyDateTo) {
      filteredPayments = filteredPayments.filter(p => dayjs(p.payment_date).isSameOrBefore(dayjs(historyDateTo), 'day'));
    }
    
    const totalPaid = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
    const unpaid = totalSalary - totalPaid;
    
    console.log('✅ Filtered stats:', { totalSalary, totalPaid, unpaid, shifts: filteredShifts.length, payments: filteredPayments.length });
    
    return { totalSalary, totalPaid, unpaid };
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // React Query hooks - MUST be declared before useMemo that uses them
  const queryClient = useQueryClient();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [shiftsEmployeeId, setShiftsEmployeeId] = useState(null);
  
  // React Query hooks for data fetching
  // Fetch all employees (both active and inactive) - pass undefined to get all
  const { data: allEmployeesData = [], isLoading: employeesLoading } = useEmployees(undefined);
  const allEmployees = allEmployeesData;
  
  // Filter employees based on active tab and search term
  const activeEmployees = useMemo(() => {
    let filtered = allEmployees.filter(emp => emp.active === 1);
    if (employeeSearchTerm.trim()) {
      const searchLower = employeeSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(emp => 
        (emp.name || '').toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [allEmployees, employeeSearchTerm]);
  
  const inactiveEmployees = useMemo(() => {
    let filtered = allEmployees.filter(emp => emp.active === 0);
    if (employeeSearchTerm.trim()) {
      const searchLower = employeeSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(emp => 
        (emp.name || '').toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [allEmployees, employeeSearchTerm]);
  
  const weekStartStr = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
  const { data: cumulativeData = [], isLoading: cumulativeLoading } = useCumulativePayments(weekStartStr);
  const { data: paymentHistoryData = [], isLoading: paymentHistoryLoading, refetch: refetchPaymentHistory } = useEmployeePayments(selectedEmployeeId);
  const { data: employeeShiftsData = [], refetch: refetchShifts } = useShifts(
    shiftsEmployeeId ? { employee_id: shiftsEmployeeId } : {}
  );
  
  // React Query mutations for employees
  const createEmployeeMutation = useCreateEmployee();
  const updateEmployeeMutation = useUpdateEmployee();
  const deleteEmployeeMutation = useDeleteEmployee();
  const toggleEmployeeActiveMutation = useToggleEmployeeActive();
  
  // React Query mutations for payments
  const createPaymentMutation = useCreatePayment();
  const updatePaymentMutation = useUpdatePayment();
  const deletePaymentMutation = useDeletePayment();

  // Calculate payment stats from React Query data (based on shifts)
  const paymentStatsCalculated = useMemo(() => {
    if (!selectedEmployeeId || !currentEmployee) {
      return { totalSalary: 0, totalPaid: 0, unpaid: 0 };
    }
    
    // Calculate total salary from ALL shifts
    let totalSalary = 0;
    if (employeeShiftsData && employeeShiftsData.length > 0) {
      employeeShiftsData.forEach(shift => {
        // Calculate hours from start_time and end_time
        const startParts = shift.start_time.split(':');
        const endParts = shift.end_time.split(':');
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        // Handle shifts that cross midnight (e.g., 16:30-00:30)
        if (endMinutes < startMinutes) {
          endMinutes += 24 * 60;
        }
        const hours = (endMinutes - startMinutes) / 60;
        
        if (hours > 0) {
          // USE STORED WAGE FROM SHIFT FOR HISTORICAL ACCURACY
          // Fallback to employee wage for older shifts that don't have hourly_wage stored
          let wageRate = shift.hourly_wage;
          
          if (wageRate === null || wageRate === undefined) {
            // For old shifts without stored wage, determine based on day
            const empData = shift.employee || currentEmployee;
            if (empData) {
              // day: 0=Sunday, 6=Saturday
              if (shift.day === 0) {
                wageRate = empData.sunday_wage || empData.hourly_wage || 0;
              } else if (shift.day === 6) {
                wageRate = empData.saturday_wage || empData.hourly_wage || 0;
              } else {
                wageRate = empData.hourly_wage || 0;
              }
            } else {
              wageRate = 0;
            }
          }

          const shiftSalary = hours * wageRate;
          totalSalary += shiftSalary;
        }
      });
    }
    
    // Get total paid from payment history
    const totalPaid = paymentHistoryData.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
    
    // Unpaid = Total salary - Total paid
    const unpaid = totalSalary - totalPaid;
    
    return {
      totalSalary: totalSalary || 0,
      totalPaid: totalPaid || 0,
      unpaid: unpaid || 0
    };
  }, [selectedEmployeeId, currentEmployee, employeeShiftsData, paymentHistoryData]);

  // Update paymentStats state when calculated stats change
  useEffect(() => {
    setPaymentStats(paymentStatsCalculated);
  }, [paymentStatsCalculated]);

  const fetchPaymentStats = async (empId, filter) => {
    // Stats are now calculated automatically from React Query data
    // This function is kept for backward compatibility
  };
  
  // Update local state when data changes
  useEffect(() => {
    if (paymentHistoryData) {
      setPaymentHistory(Array.isArray(paymentHistoryData) ? paymentHistoryData : []);
    }
  }, [paymentHistoryData]);

  const fetchPaymentHistory = async (empId) => {
    // Set employee ID to trigger React Query fetch
    setSelectedEmployeeId(empId);
    // Fetch stats will be handled separately
    await fetchPaymentStats(empId, 'current_week');
  };

  // Update local state when data changes
  useEffect(() => {
    if (employeeShiftsData) {
      setEmployeeShifts(Array.isArray(employeeShiftsData) ? employeeShiftsData : []);
    }
  }, [employeeShiftsData]);

  // Refetch shifts when unified modal is open (for any tab, to ensure stats are correct)
  useEffect(() => {
    if (unifiedModal.open && unifiedModal.emp) {
      // Invalidate cache and refetch shifts to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['shifts', 'list', { employee_id: unifiedModal.emp.id }] });
      refetchShifts();
    }
  }, [unifiedModal.open, unifiedModal.emp, refetchShifts, queryClient]);

  // Fetch fixed schedule when unified modal is open on fixed schedule tab
  useEffect(() => {
    if (unifiedModal.open && unifiedModal.tab === 3 && unifiedModal.emp) {
      fetchFixedSchedule(unifiedModal.emp.id);
    }
  }, [unifiedModal.open, unifiedModal.tab, unifiedModal.emp]);

  const openPaymentHistoryModal = async (emp) => {
    setPaymentHistoryModal({ open: true, emp });
    setPaymentStatsFilter('current_week'); // Default to current week
    setCurrentEmployee(emp);
    setShiftsEmployeeId(emp.id); // Trigger shifts fetch
    await fetchPaymentHistory(emp.id);
    
    // Invalidate shifts cache to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ['shifts'] });
  };

  const handlePaymentStatsFilterChange = async (filter) => {
    setPaymentStatsFilter(filter);
    if (paymentHistoryModal.emp) {
      await fetchPaymentStats(paymentHistoryModal.emp.id, filter);
    }
  };

  const openUnifiedModal = async (emp, tab = 0) => {
    console.log('🚪 Opening unified modal for:', emp.name);
    setCurrentEmployee(emp);
    setUnifiedModal({ open: true, emp, tab });
    
    // Load edit form
    setEditForm({
      name: emp.name,
      position: emp.position,
      hourly_wage: emp.hourly_wage,
      saturday_wage: emp.saturday_wage || '',
      sunday_wage: emp.sunday_wage || '',
      shift_preference: emp.shift_preference,
      max_days_per_week: emp.max_days_per_week || 7,
      available_days: emp.available_days || '0,1,2,3,4,5,6',
      shift_preferences: emp.shift_preferences || 'any',
      notes: emp.notes || ''
    });
    
    // Trigger React Query fetches (data will be loaded automatically)
    setSelectedEmployeeId(emp.id);
    setShiftsEmployeeId(emp.id);
    
    // Invalidate shifts cache to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ['shifts'] });
  };

  const handleOpenEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentForm({
      payment_date: payment.payment_date,
      amount: payment.amount,
      payment_method: payment.payment_method,
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
    setEditPaymentDialog(true);
  };

  const handleCloseEditPayment = () => {
    setEditPaymentDialog(false);
    setEditingPayment(null);
    setEditPaymentForm({
      payment_date: '',
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleOpenNewPayment = async () => {
    setNewPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
    setSelectedWeeks([]);
    
    // Get employee from either unifiedModal or paymentHistoryModal
    const currentEmp = unifiedModal.emp || paymentHistoryModal.emp;
    
    // Get unpaid weeks from React Query cumulative data
    if (currentEmp && cumulativeData) {
      try {
        // Find this employee's unpaid weeks
        const employeeData = cumulativeData.find(e => e.id === currentEmp.id);
        if (employeeData && employeeData.unpaid_weeks) {
          // Sort by week_start (oldest first) for FIFO display
          const sortedUnpaid = employeeData.unpaid_weeks.sort((a, b) => 
            new Date(a.week_start) - new Date(b.week_start)
          );
          setUnpaidWeeks(sortedUnpaid);
        } else {
          setUnpaidWeeks([]);
        }
      } catch (error) {
        console.error('Error processing unpaid weeks:', error);
        setUnpaidWeeks([]);
      }
    } else {
      setUnpaidWeeks([]);
    }
    
    setNewPaymentDialog(true);
  };

  const handleCloseNewPayment = () => {
    setNewPaymentDialog(false);
    setNewPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleSaveNewPayment = async () => {
    if (!newPaymentForm.amount || parseFloat(newPaymentForm.amount) <= 0) {
      showAlert('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
      return;
    }

    // Get employee from either unifiedModal or paymentHistoryModal
    const currentEmp = unifiedModal.emp || paymentHistoryModal.emp;
    
    if (!currentEmp) {
      showAlert('Σφάλμα: Δεν βρέθηκε ο υπάλληλος', 'error');
      return;
    }

    try {
      // Automatic FIFO allocation to oldest unpaid weeks first
      let remainingAmount = parseFloat(newPaymentForm.amount);
      const allocations = [];
      
      // Sort unpaid weeks by date (oldest first) - already sorted from handleOpenNewPayment
      for (const week of unpaidWeeks) {
        if (remainingAmount <= 0) break;
        
        const weekUnpaid = week.unpaid;
        const allocationAmount = Math.min(remainingAmount, weekUnpaid);
        
        allocations.push({
          week_start: week.week_start,
          allocated_amount: allocationAmount
        });
        
        remainingAmount -= allocationAmount;
      }
      
      // If there's still remaining amount (overpayment or no unpaid weeks), allocate to current week
      if (remainingAmount > 0 || allocations.length === 0) {
        const currentWeekStart = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
        if (allocations.length === 0) {
          allocations.push({
            week_start: currentWeekStart,
            allocated_amount: parseFloat(newPaymentForm.amount)
          });
        } else {
          // Ελέγχουμε αν η εβδομάδα υπάρχει ήδη στις allocations (αποφυγή duplicate week_start)
          const existingAlloc = allocations.find(a => a.week_start === currentWeekStart);
          if (existingAlloc) {
            existingAlloc.allocated_amount += remainingAmount;
          } else {
            allocations.push({
              week_start: currentWeekStart,
              allocated_amount: remainingAmount
            });
          }
        }
      }
      
      // Validate allocations sum matches payment amount
      const allocationsSum = allocations.reduce((sum, alloc) => sum + parseFloat(alloc.allocated_amount), 0);
      const paymentAmount = parseFloat(newPaymentForm.amount);
      
      if (Math.abs(allocationsSum - paymentAmount) > 0.01) {
        console.error('❌ Allocations sum mismatch:', { allocationsSum, paymentAmount, allocations });
        showAlert(`Σφάλμα: Το άθροισμα των κατανομών (€${allocationsSum.toFixed(2)}) δεν ισούται με το ποσό πληρωμής (€${paymentAmount.toFixed(2)})`, 'error');
        return;
      }
      
      // Prepare payment data with allocations
      // Ensure payment_date is in YYYY-MM-DD format (remove time if present)
      let paymentDate = newPaymentForm.payment_date;
      if (paymentDate && paymentDate.includes('T')) {
        paymentDate = paymentDate.split('T')[0];
      }
      
      const paymentData = {
        employee_id: currentEmp.id,
        payment_date: paymentDate,
        amount: paymentAmount,
        payment_method: newPaymentForm.payment_method || 'cash',
        reference: newPaymentForm.reference || null,
        notes: newPaymentForm.notes || null,
        allocations: allocations
      };
      
      // Create payment with allocations using React Query mutation
      console.log('📤 Sending payment data:', JSON.stringify(paymentData, null, 2));
      const result = await createPaymentMutation.mutateAsync(paymentData);
      
      console.log('✅ Payment created successfully:', result);

      showAlert('Η πληρωμή καταχωρήθηκε επιτυχώς', 'success');
      handleCloseNewPayment();
      
      // React Query will automatically refetch payment history
      if (currentEmp) {
        refetchPaymentHistory();
      }
    } catch (error) {
      console.error('❌ Error saving payment:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Error stack:', error.stack);
      
      // Extract error message
      let errorMessage = 'Άγνωστο σφάλμα';
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Show more detailed error if available
      if (error.details) {
        console.error('Backend error details:', error.details);
        errorMessage += ` (Details: ${JSON.stringify(error.details)})`;
      }
      
      showAlert(`Σφάλμα κατά την αποθήκευση της πληρωμής: ${errorMessage}`, 'error');
    }
  };

  const handleSaveEditPayment = async () => {
    if (!editPaymentForm.amount || parseFloat(editPaymentForm.amount) <= 0) {
      showAlert('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
      return;
    }

    if (!editingPayment) {
      showAlert('Δεν υπάρχει πληρωμή προς επεξεργασία', 'error');
      return;
    }

    try {
      console.log('🔄 Updating payment:', { id: editingPayment.id, data: editPaymentForm });
      const result = await updatePaymentMutation.mutateAsync({
        id: editingPayment.id,
        data: {
          ...editPaymentForm,
          amount: parseFloat(editPaymentForm.amount),
          employee_id: editingPayment.employee_id // Include employee_id for cache invalidation
        }
      });
      console.log('✅ Payment updated successfully:', result);
      showAlert('Η πληρωμή ενημερώθηκε επιτυχώς!', 'success');
      handleCloseEditPayment();
      // Don't manually refetch - React Query will do it automatically via cache invalidation
    } catch (error) {
      console.error('❌ Error updating payment:', error);
      const errorMessage = error.message || error.toString();
      // Only show error if it's a real error, not a successful update
      if (!errorMessage.includes('success') && !errorMessage.includes('200')) {
        showAlert(`Σφάλμα ενημέρωσης πληρωμής: ${errorMessage}`, 'error');
      }
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την πληρωμή;')) {
      return;
    }

    try {
      console.log('🔄 Deleting payment:', paymentId);
      await deletePaymentMutation.mutateAsync(paymentId);
      console.log('✅ Payment deleted successfully');
      showAlert('Η πληρωμή διαγράφηκε επιτυχώς!', 'success');
      // Don't manually refetch - React Query will do it automatically via cache invalidation
    } catch (error) {
      console.error('❌ Error deleting payment:', error);
      const errorMessage = error.message || error.toString();
      // Only show error if it's a real error, not a successful delete
      if (!errorMessage.includes('success') && !errorMessage.includes('200')) {
        showAlert(`Σφάλμα διαγραφής πληρωμής: ${errorMessage}`, 'error');
      }
    }
  };

  const handleEditChange = e => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleDayChange = (dayValue) => {
    const currentDays = editForm.available_days ? editForm.available_days.split(',').map(Number) : [];
    const updatedDays = currentDays.includes(dayValue)
      ? currentDays.filter(d => d !== dayValue)
      : [...currentDays, dayValue];
    
    setEditForm({ ...editForm, available_days: updatedDays.sort().join(',') });
  };

  const handleEditSave = async () => {
    // Get employee from either editModal or unifiedModal
    const emp = editModal.emp || unifiedModal.emp;
    
    if (!emp) {
      showAlert('Σφάλμα: Δεν βρέθηκε ο υπάλληλος', 'error');
      return;
    }
    
    const payload = {
      name: editForm.name,
      email: editForm.email || null,
      position: editForm.position,
      hourly_wage: parseFloat(editForm.hourly_wage) || 0,
      saturday_wage: editForm.saturday_wage ? parseFloat(editForm.saturday_wage) : null,
      sunday_wage: editForm.sunday_wage ? parseFloat(editForm.sunday_wage) : null,
      shift_preference: editForm.shift_preference,
      max_days_per_week: emp.max_days_per_week,
      available_days: emp.available_days,
      shift_preferences: emp.shift_preferences,
      notes: emp.notes,
      active: emp.active
    };
    
    try {
      await updateEmployeeMutation.mutateAsync({ id: emp.id, data: payload });
      closeEditModal();
      // Close unified modal if open
      if (unifiedModal.open) {
        setUnifiedModal({ open: false, emp: null, tab: 0 });
      }
      showAlert('Υπάλληλος ενημερώθηκε επιτυχώς', 'success');
    } catch (error) {
      console.error('Error updating employee:', error);
      const errorMessage = error.message || 'Σφάλμα ενημέρωσης υπαλλήλου';
      showAlert(errorMessage, 'error');
    }
  };

  const handlePreferencesSave = async () => {
    const { emp } = preferencesModal;
    const payload = {
      name: emp.name,
      email: emp.email || null,
      position: emp.position,
      hourly_wage: emp.hourly_wage,
      shift_preference: emp.shift_preference,
      max_days_per_week: editForm.max_days_per_week,
      available_days: editForm.available_days,
      shift_preferences: editForm.shift_preferences,
      notes: editForm.notes,
      active: emp.active
    };
    
    try {
      await updateEmployeeMutation.mutateAsync({ id: emp.id, data: payload });
      closePreferencesModal();
      showAlert('Προτιμήσεις ενημερώθηκαν επιτυχώς', 'success');
    } catch (error) {
      console.error('Error updating preferences:', error);
      showAlert('Σφάλμα ενημέρωσης προτιμήσεων', 'error');
    }
  };

  // ========== FIXED SCHEDULE FUNCTIONS ==========
  const DAYS_FOR_SCHEDULE = [
    { value: 1, label: 'Δευτέρα' },
    { value: 2, label: 'Τρίτη' },
    { value: 3, label: 'Τετάρτη' },
    { value: 4, label: 'Πέμπτη' },
    { value: 5, label: 'Παρασκευή' },
    { value: 6, label: 'Σάββατο' },
    { value: 0, label: 'Κυριακή' }
  ];

  const fetchFixedSchedule = async (employeeId) => {
    try {
      console.log('Fetching fixed schedule for employee:', employeeId);
      const response = await fetchWithAuth(`/api/fixed-schedules/${employeeId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch fixed schedule');
      }
      
      const schedules = await response.json();
      console.log('Fixed schedules received:', schedules);
      
      // Build days object from schedules
      const days = {};
      DAYS_FOR_SCHEDULE.forEach(day => {
        const schedule = schedules.find(s => s.day_of_week === day.value);
        days[day.value] = {
          enabled: !!schedule,
          start_time: schedule?.start_time || '',
          end_time: schedule?.end_time || ''
        };
      });
      
      // Check if there are any schedules - if yes, the fixed schedule is enabled
      const hasAnySchedule = schedules.length > 0;
      
      setFixedSchedule({
        enabled: hasAnySchedule,
        days
      });
      
      console.log('Fixed schedule state set:', { enabled: hasAnySchedule, days });
    } catch (error) {
      console.error('Error fetching fixed schedule:', error);
      showAlert('Σφάλμα φόρτωσης σταθερού προγράμματος', 'error');
    }
  };

  const handleSaveFixedSchedule = async () => {
    if (!unifiedModal.emp) return;
    
    setSavingFixedSchedule(true);
    try {
      const schedules = Object.entries(fixedSchedule.days)
        .filter(([_, day]) => day.enabled && day.start_time && day.end_time)
        .map(([dayValue, day]) => ({
          day_of_week: parseInt(dayValue),
          start_time: day.start_time,
          end_time: day.end_time,
          enabled: true
        }));
      
      const response = await fetchWithAuth(`/api/fixed-schedules/${unifiedModal.emp.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          has_fixed_schedule: fixedSchedule.enabled,
          schedules
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save fixed schedule');
      }
      
      showAlert('Το σταθερό πρόγραμμα αποθηκεύτηκε επιτυχώς', 'success');
      
      // Invalidate employees query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      
      // Close the modal after successful save
      setUnifiedModal({ open: false, emp: null, tab: 0 });
    } catch (error) {
      console.error('Error saving fixed schedule:', error);
      showAlert('Σφάλμα αποθήκευσης σταθερού προγράμματος', 'error');
    } finally {
      setSavingFixedSchedule(false);
    }
  };

  // Employees are now fetched via React Query hook above - no need for useEffect

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdd = async () => {
    if (!form.name || !form.hourly_wage) {
      showAlert('Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία', 'error');
      return;
    }

    if (!canAddEmployee()) {
      setPlanLimitModal(true);
      return;
    }
    
    try {
      // Convert numeric fields from strings to numbers
      const employeeData = {
        ...form,
        hourly_wage: parseFloat(form.hourly_wage),
        active: 1
      };
      const newEmp = await createEmployeeMutation.mutateAsync(employeeData);
      setForm({ 
        name: '', 
        email: '',
        position: positions[0] || '', 
        shift_preference: shifts[0] || '', 
        hourly_wage: '',
        max_days_per_week: 7,
        available_days: '0,1,2,3,4,5,6',
        shift_preferences: 'any',
        notes: ''
      });
      showAlert('Υπάλληλος προστέθηκε επιτυχώς', 'success');
    } catch (error) {
      console.error('Error adding employee:', error);
      if (error?.code === 'PLAN_EMPLOYEE_LIMIT' || error?.message?.includes('όριο')) {
        setPlanLimitModal(true);
        return;
      }
      showAlert(error?.message || 'Σφάλμα προσθήκης υπαλλήλου', 'error');
    }
  };

  const handleToggleActive = async (employeeId) => {
    const employee = allEmployees.find(e => e.id === employeeId);
    const action = employee.active === 1 ? 'απενεργοποίηση' : 'ενεργοποίηση';

    if (employee.active !== 1 && !canAddEmployee()) {
      setPlanLimitModal(true);
      return;
    }
    
    if (window.confirm(`Είστε σίγουροι ότι θέλετε την ${action} αυτού του υπαλλήλου;`)) {
      try {
        await toggleEmployeeActiveMutation.mutateAsync(employeeId);
        showAlert(`Ο υπάλληλος ${action === 'ενεργοποίηση' ? 'ενεργοποιήθηκε' : 'απενεργοποιήθηκε'} επιτυχώς`, 'success');
      } catch (error) {
        console.error(`Error toggling employee active status:`, error);
        if (error?.code === 'PLAN_EMPLOYEE_LIMIT' || error?.message?.includes('όριο')) {
          setPlanLimitModal(true);
          return;
        }
        showAlert(error?.message || `Σφάλμα ${action} υπαλλήλου`, 'error');
      }
    }
  };

  const handleDelete = async id => {
    if (window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε οριστικά αυτόν τον υπάλληλο; Αυτό θα διαγράψει όλα τα δεδομένα του.')) {
      try {
        await deleteEmployeeMutation.mutateAsync(id);
        showAlert('Υπάλληλος διαγράφηκε οριστικά', 'success');
      } catch (error) {
        console.error('Error deleting employee:', error);
        showAlert('Σφάλμα διαγραφής υπαλλήλου', 'error');
      }
    }
  };

  const showAlert = (message, severity) => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 3000);
  };

  // Function to determine shift type based on start time
  const determineShiftType = (startTime) => {
    const [hours] = startTime.split(':').map(Number);
    // Πρωινή βάρδια: 05:00 - 14:59
    // Απογευματινή βάρδια: 15:00 - 04:59
    if (hours >= 5 && hours < 15) {
      return 'morning';
    } else {
      return 'evening';
    }
  };

  const handleQuickAddToSchedule = async () => {
    const { emp } = quickAddModal;
    const { selectedDays, dayTimes, weekOffset } = quickAddForm;

    if (selectedDays.length === 0) {
      showAlert('Επιλέξτε τουλάχιστον μία ημέρα', 'warning');
      return;
    }

    // Validate that all selected days have times
    for (const day of selectedDays) {
      const times = dayTimes[day];
      if (!times?.startTime || !times?.endTime) {
        showAlert('Συμπληρώστε ώρες έναρξης και λήξης για όλες τις επιλεγμένες ημέρες', 'warning');
        return;
      }
    }

    // Get target week start (Monday) based on weekOffset
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + diff + (weekOffset * 7));
    
    // Format date in local timezone to avoid timezone offset issues
    const year = weekStart.getFullYear();
    const month = String(weekStart.getMonth() + 1).padStart(2, '0');
    const day = String(weekStart.getDate()).padStart(2, '0');
    const weekStartStr = `${year}-${month}-${day}`;
    
    console.log('Today:', today, 'dayOfWeek:', dayOfWeek, 'diff:', diff);
    console.log('Week start calculated:', weekStartStr, 'with offset:', weekOffset);

    try {
      const shiftsToCreate = selectedDays.map(dayOfWeek => {
        const times = dayTimes[dayOfWeek];
        const shiftType = determineShiftType(times.startTime);
        
        return {
          day: dayOfWeek,
          employee_id: emp.id,
          start_time: times.startTime,
          end_time: times.endTime,
          week_start: weekStartStr,
          shift_type: shiftType
        };
      });

      let successCount = 0;
      let duplicateCount = 0;
      let errorCount = 0;

      for (const shift of shiftsToCreate) {
        try {
          console.log('Creating shift:', shift);
          const response = await fetchWithAuth('/api/shifts', {
            method: 'POST',
            body: JSON.stringify(shift)
          });

          const data = await response.json();
          console.log('Response:', response.status, data);
          console.log('Shift created successfully with ID:', data.id);
          console.log('Week start:', data.week_start, 'Day:', data.day);

          if (response.ok) {
            successCount++;
          } else if (response.status === 409 || data.error?.includes('UNIQUE constraint')) {
            duplicateCount++;
          } else {
            console.error('Failed to create shift:', response.status, data);
            errorCount++;
          }
        } catch (err) {
          console.error('Error creating shift:', err);
          errorCount++;
        }
      }

      if (successCount > 0 && duplicateCount === 0 && errorCount === 0) {
        showAlert(`Ο ${emp.name} προστέθηκε στο πρόγραμμα (${successCount} βάρδιες)!`, 'success');
        closeQuickAddModal();
      } else if (successCount > 0) {
        showAlert(`Προστέθηκαν ${successCount} βάρδιες. ${duplicateCount > 0 ? `${duplicateCount} υπήρχαν ήδη.` : ''}`, 'warning');
        closeQuickAddModal();
      } else if (duplicateCount > 0) {
        showAlert('Όλες οι βάρδιες υπάρχουν ήδη', 'warning');
      } else {
        showAlert('Σφάλμα προσθήκης στο πρόγραμμα', 'error');
      }
    } catch (error) {
      console.error('Error adding to schedule:', error);
      showAlert('Σφάλμα προσθήκης στο πρόγραμμα', 'error');
    }
  };

  const handleDayToggle = (day) => {
    const { selectedDays } = quickAddForm;
    if (selectedDays.includes(day)) {
      setQuickAddForm({
        ...quickAddForm,
        selectedDays: selectedDays.filter(d => d !== day)
      });
    } else {
      setQuickAddForm({
        ...quickAddForm,
        selectedDays: [...selectedDays, day]
      });
    }
  };

  const getWeekDateRange = (weekOffset) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff + (weekOffset * 7));
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date) => {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    return `${formatDate(monday)} - ${formatDate(sunday)}`;
  };

  const getAvailableDaysText = (availableDays) => {
    if (!availableDays) return '';
    const days = availableDays.split(',').map(Number);
    if (days.length === 7) return '';
    return days.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(', ');
  };

  const getShiftPreferenceText = (preference) => {
    const result = SHIFT_PREFERENCES.find(p => p.value === preference)?.label || preference;
    if (result === 'Όλες οι βάρδιες') return '';
    return result;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Access denied if user can't view employees
  if (!canView) {
    return <AccessDenied message="Δεν έχετε πρόσβαση στη διαχείριση προσωπικού." />;
  }

  return (
    <Box sx={{ maxWidth: '1600px', width: '100%' }}>
      {alert && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Typography 
        variant={isMobile ? 'h5' : 'h4'} 
        sx={{ mb: { xs: 2, md: 4 }, fontWeight: 600 }}
      >
        Προσωπικό
      </Typography>
      
      {/* Add Employee Form - Only visible if user can create employees */}
      {canCreate && (
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: isMobile ? 'pointer' : 'default',
            mb: (!isMobile || addFormExpanded) ? 2 : 0
          }}
          onClick={() => isMobile && setAddFormExpanded(!addFormExpanded)}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Προσθήκη Νέου Υπαλλήλου
          </Typography>
          {isMobile && (
            <IconButton size="small">
              {addFormExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        
        <Collapse in={!isMobile || addFormExpanded}>
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            <Grid item xs={12} sm={6} md={2.5}>
            <TextField 
              label="Ονοματεπώνυμο" 
              name="name" 
              value={form.name} 
              onChange={handleChange}
              fullWidth
              required
                size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
            <TextField 
              label="Email" 
              name="email" 
              type="email"
              value={form.email} 
              onChange={handleChange}
              fullWidth
              placeholder="employee@email.com"
                size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel>Πόστο</InputLabel>
              <Select name="position" value={form.position} label="Πόστο" onChange={handleChange}>
                {positions && positions.map(pos => <MenuItem key={pos} value={pos}>{pos}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Βάρδια</InputLabel>
                <Select name="shift_preference" value={form.shift_preference} label="Βάρδια" onChange={handleChange}>
                {shifts && shifts.map(shift => <MenuItem key={shift} value={shift}>{shift}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
            <Grid item xs={6} sm={6} md={2}>
            <TextField 
                label="€/ώρα" 
              name="hourly_wage" 
              type="number" 
              value={form.hourly_wage} 
              onChange={handleChange}
              fullWidth
              required
                size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
            <Grid item xs={6} sm={6} md={2}>
            <TextField 
                label="Μέγ. ημέρες" 
              name="max_days_per_week" 
              type="number" 
              value={form.max_days_per_week} 
              onChange={handleChange}
              inputProps={{ min: 1, max: 7 }}
              fullWidth
                size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} md={1}>
              <Button 
                variant="contained" 
                onClick={handleAdd} 
                fullWidth 
                sx={{ height: { xs: '40px', md: '56px' } }}
              >
                {isMobile ? '+' : 'Προσθήκη'}
            </Button>
          </Grid>
        </Grid>
        </Collapse>
      </Paper>
      )}

      {/* Employee Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          px: { xs: 1, md: 2 }, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' }, 
          justifyContent: 'space-between' 
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="employee tabs"
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{ minHeight: { xs: 42, md: 48 } }}
          >
            <Tab 
              label={isMobile ? `Ενεργοί (${activeEmployees.length})` : `Ενεργοί Υπάλληλοι (${activeEmployees.length})`} 
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                minHeight: { xs: 42, md: 48 },
                py: { xs: 1, md: 1.5 }
              }} 
            />
            <Tab 
              label={isMobile ? `Ανενεργοί (${inactiveEmployees.length})` : `Ανενεργοί Υπάλληλοι (${inactiveEmployees.length})`} 
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                minHeight: { xs: 42, md: 48 },
                py: { xs: 1, md: 1.5 }
              }} 
            />
          </Tabs>
          <TextField
            size="small"
            placeholder="Αναζήτηση..."
            value={employeeSearchTerm}
            onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            sx={{ 
              minWidth: { xs: 'auto', md: 220 }, 
              my: { xs: 1, md: 1 },
              mx: { xs: 1, md: 0 }
            }}
            InputProps={{
              startAdornment: (
                <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box>
              ),
            }}
          />
        </Box>
        
        {/* Active Employees Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={{ xs: 1, md: 2 }}>
            {activeEmployees.map((emp) => (
              <Grid item xs={12} sm={6} md={4} key={emp.id}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 1.5, md: 2.5 }, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: 'fit-content',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1, md: 2 } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography 
                          variant="subtitle1" 
                          onClick={() => openUnifiedModal(emp, 0)}
                          title="Διαχείριση Υπαλλήλου"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '140px', sm: '180px', md: '200px' },
                            cursor: 'pointer',
                            color: 'text.primary',
                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                          }}
                        >
                          {emp.name}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => openQuickAddModal(emp)}
                          title="Προσθήκη στο Πρόγραμμα"
                          sx={{ 
                            bgcolor: 'success.light',
                            color: 'success.dark',
                            width: { xs: 22, md: 24 },
                            height: { xs: 22, md: 24 },
                            '&:hover': { bgcolor: 'success.main', color: 'white' }
                          }}
                        >
                          <AddIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                        </IconButton>
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                      >
                        {emp.position}
                      </Typography>
                      {!isMobile && getAvailableDaysText(emp.available_days) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {getAvailableDaysText(emp.available_days)}
                        </Typography>
                      )}
                      {!isMobile && getShiftPreferenceText(emp.shift_preferences) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {getShiftPreferenceText(emp.shift_preferences)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 0.25, md: 0.5 }, flexShrink: 0 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => openUnifiedModal(emp, 0)}
                        title="Διαχείριση Υπαλλήλου"
                        sx={{ 
                          color: 'primary.main', 
                          '&:hover': { bgcolor: 'primary.light' },
                          p: { xs: 0.5, md: 1 }
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleActive(emp.id)}
                        title="Απενεργοποίηση"
                        sx={{ 
                          color: 'warning.main', 
                          '&:hover': { bgcolor: 'warning.light' },
                          p: { xs: 0.5, md: 1 }
                        }}
                      >
                        <PersonOffIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(emp.id)}
                        title="Οριστική διαγραφή"
                        sx={{ 
                          color: 'error.main', 
                          '&:hover': { bgcolor: 'error.light' },
                          p: { xs: 0.5, md: 1 }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Inactive Employees Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={{ xs: 1, md: 2 }}>
            {inactiveEmployees.map((emp) => (
              <Grid item xs={12} sm={6} md={4} key={emp.id}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 1.5, md: 2.5 }, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: 'fit-content',
                    backgroundColor: '#fafafa',
                    opacity: 0.7
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle1" 
                          onClick={() => openUnifiedModal(emp, 0)}
                          title="Διαχείριση Υπαλλήλου"
                          sx={{ 
                            fontWeight: 600, 
                            color: 'text.secondary',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: '140px', sm: '180px', md: '200px' },
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                          }}
                        >
                          {emp.name}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                          sx={{ 
                          color: 'text.secondary', 
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                      >
                        {emp.position}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 0.25, md: 0.5 }, flexShrink: 0 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => openUnifiedModal(emp, 0)}
                        title="Διαχείριση Υπαλλήλου"
                        sx={{ 
                          color: 'primary.main', 
                          '&:hover': { bgcolor: 'primary.light' },
                          p: { xs: 0.5, md: 1 }
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleActive(emp.id)}
                        title="Επανενεργοποίηση"
                        sx={{ 
                          color: 'success.main', 
                          '&:hover': { bgcolor: 'success.light' },
                          p: { xs: 0.5, md: 1 }
                        }}
                      >
                        <PersonIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(emp.id)}
                        title="Οριστική διαγραφή"
                        sx={{ 
                          color: 'error.main', 
                          '&:hover': { bgcolor: 'error.light' },
                          p: { xs: 0.5, md: 1 }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Paper>

      {/* Edit Employee Modal */}
      <Dialog open={editModal.open} onClose={closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>Επεξεργασία Υπαλλήλου</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Ονοματεπώνυμο"
              name="name"
              value={editForm.name || ''}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={editForm.email || ''}
              onChange={handleEditChange}
              fullWidth
              placeholder="employee@email.com"
              helperText="Για αποστολή ειδοποιήσεων βαρδιών"
            />
            <FormControl fullWidth>
              <InputLabel>Πόστο</InputLabel>
              <Select
                name="position"
                value={editForm.position || ''}
                label="Πόστο"
                onChange={handleEditChange}
              >
                {positions && positions.map(pos => <MenuItem key={pos} value={pos}>{pos}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Αμοιβή ανά ώρα"
              name="hourly_wage"
              type="number"
              value={editForm.hourly_wage || ''}
              onChange={handleEditChange}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Προτίμηση Βάρδιας</InputLabel>
              <Select
                name="shift_preference"
                value={editForm.shift_preference || ''}
                label="Προτίμηση Βάρδιας"
                onChange={handleEditChange}
              >
                {shifts && shifts.map(shift => <MenuItem key={shift} value={shift}>{shift}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal}>Άκυρο</Button>
          <Button onClick={handleEditSave} variant="contained">Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      {/* Preferences Modal */}
      <Dialog open={preferencesModal.open} onClose={closePreferencesModal} maxWidth="md" fullWidth>
        <DialogTitle>Ρυθμίσεις Προτιμήσεων - {preferencesModal.emp?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Max Days per Week */}
            <Box>
              <Typography variant="h6" gutterBottom>Μέγιστες Ημέρες Εργασίας</Typography>
              <TextField
                label="Μέγιστες ημέρες ανά εβδομάδα"
                name="max_days_per_week"
                type="number"
                value={editForm.max_days_per_week || 7}
                onChange={handleEditChange}
                inputProps={{ min: 1, max: 7 }}
                fullWidth
                helperText="Πόσες ημέρες μπορεί να δουλέψει το πολύ την εβδομάδα"
              />
            </Box>

            <Divider />

            {/* Available Days */}
            <Box>
              <Typography variant="h6" gutterBottom>Διαθέσιμες Ημέρες</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Επιλέξτε τις ημέρες που μπορεί να δουλέψει ο υπάλληλος
              </Typography>
              <FormGroup row>
                {DAYS_OF_WEEK.map((day) => {
                  const isChecked = editForm.available_days 
                    ? editForm.available_days.split(',').map(Number).includes(day.value)
                    : true;
                  return (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleDayChange(day.value)}
                        />
                      }
                      label={day.label}
                    />
                  );
                })}
              </FormGroup>
            </Box>

            <Divider />

            {/* Shift Preferences */}
            <Box>
              <Typography variant="h6" gutterBottom>Προτιμήσεις Βάρδιας</Typography>
              <FormControl fullWidth>
                <InputLabel>Προτίμηση Βάρδιας</InputLabel>
                <Select
                  name="shift_preferences"
                  value={editForm.shift_preferences || 'any'}
                  label="Προτίμηση Βάρδιας"
                  onChange={handleEditChange}
                >
                  {SHIFT_PREFERENCES.map((pref) => (
                    <MenuItem key={pref.value} value={pref.value}>
                      {pref.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Notes */}
            <Box>
              <Typography variant="h6" gutterBottom>Σημειώσεις</Typography>
              <TextField
                label="Σημειώσεις και ειδικές απαιτήσεις"
                name="notes"
                value={editForm.notes || ''}
                onChange={handleEditChange}
                multiline
                rows={3}
                fullWidth
                placeholder="π.χ. Δεν μπορεί να δουλέψει μετά τις 20:00, προτιμά συγκεκριμένες βάρδιες..."
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreferencesModal}>Άκυρο</Button>
          <Button onClick={handlePreferencesSave} variant="contained">Αποθήκευση</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add to Schedule Modal */}
      <Dialog open={quickAddModal.open} onClose={closeQuickAddModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Γρήγορη Προσθήκη στο Πρόγραμμα
          {quickAddModal.emp && (
            <Typography variant="body2" color="textSecondary">
              {quickAddModal.emp.name} - {quickAddModal.emp.position}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Week Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Επιλέξτε Εβδομάδα:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <IconButton 
                  onClick={() => setQuickAddForm({ ...quickAddForm, weekOffset: quickAddForm.weekOffset - 1 })}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="body1" sx={{ minWidth: '200px', textAlign: 'center', fontWeight: 'medium' }}>
                  {getWeekDateRange(quickAddForm.weekOffset)}
                </Typography>
                <IconButton 
                  onClick={() => setQuickAddForm({ ...quickAddForm, weekOffset: quickAddForm.weekOffset + 1 })}
                  size="small"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Days Selection with Individual Times */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Επιλέξτε Ημέρες και Ωράριο:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="ΩΩ:ΛΛ"
                    sx={{ width: '80px' }}
                    inputProps={{ style: { fontSize: '0.875rem' } }}
                    id="bulk-start-time"
                  />
                  <Typography variant="body2">—</Typography>
                  <TextField
                    size="small"
                    placeholder="ΩΩ:ΛΛ"
                    sx={{ width: '80px' }}
                    inputProps={{ style: { fontSize: '0.875rem' } }}
                    id="bulk-end-time"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const bulkStart = document.getElementById('bulk-start-time').value;
                      const bulkEnd = document.getElementById('bulk-end-time').value;
                      if (bulkStart && bulkEnd) {
                        const newDayTimes = { ...quickAddForm.dayTimes };
                        DAYS_OF_WEEK.forEach(day => {
                          newDayTimes[day.value] = {
                            startTime: bulkStart,
                            endTime: bulkEnd
                          };
                        });
                        setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                      }
                    }}
                  >
                    Εφαρμογή σε Όλες
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {DAYS_OF_WEEK.map((day) => (
                  <Box 
                    key={day.value}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 1.5,
                      border: quickAddForm.selectedDays.includes(day.value) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: quickAddForm.selectedDays.includes(day.value) ? '#f0f7ff' : 'transparent'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={quickAddForm.selectedDays.includes(day.value)}
                          onChange={() => handleDayToggle(day.value)}
                        />
                      }
                      label={<Typography sx={{ minWidth: '80px', fontWeight: 'medium' }}>{day.label}</Typography>}
                      sx={{ m: 0 }}
                    />
                    <TextField
                      label="Έναρξη"
                      type="text"
                      size="small"
                      value={quickAddForm.dayTimes[day.value]?.startTime || ''}
                      onChange={(e) => {
                        const newDayTimes = { ...quickAddForm.dayTimes };
                        newDayTimes[day.value] = {
                          ...newDayTimes[day.value],
                          startTime: e.target.value
                        };
                        setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                      }}
                      placeholder="09:00"
                      sx={{ width: '120px' }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                    <TextField
                      label="Λήξη"
                      type="text"
                      size="small"
                      value={quickAddForm.dayTimes[day.value]?.endTime || ''}
                      onChange={(e) => {
                        const newDayTimes = { ...quickAddForm.dayTimes };
                        newDayTimes[day.value] = {
                          ...newDayTimes[day.value],
                          endTime: e.target.value
                        };
                        setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                      }}
                      placeholder="17:00"
                      sx={{ width: '120px' }}
                      InputLabelProps={{ shrink: true }}
                    />
                    {presetHours && presetHours.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {presetHours.map((preset, idx) => {
                          // Parse preset string (e.g., "7:30-16:30")
                          const [startTime, endTime] = preset.split('-');
                          return (
                            <Chip
                              key={idx}
                              label={preset}
                              size="small"
                              onClick={() => {
                                const newDayTimes = { ...quickAddForm.dayTimes };
                                newDayTimes[day.value] = {
                                  startTime: startTime,
                                  endTime: endTime
                                };
                                setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                              }}
                              sx={{ 
                                cursor: 'pointer',
                                fontSize: '0.7rem',
                                height: '22px'
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQuickAddModal}>Άκυρο</Button>
          <Button onClick={handleQuickAddToSchedule} variant="contained" color="success">
            Προσθήκη στο Πρόγραμμα
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment History Modal */}
      <Dialog 
        open={paymentHistoryModal.open} 
        onClose={closePaymentHistoryModal} 
        maxWidth="md" 
        fullWidth
      >
        {/* Payment History Modal */}
        <DialogTitle>
          Ιστορικό Πληρωμών
          {paymentHistoryModal.emp && (
            <Typography variant="body2" color="textSecondary">
              {paymentHistoryModal.emp.name} - {paymentHistoryModal.emp.position}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            
            {/* Filter Selector */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant={paymentStatsFilter === 'current_week' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handlePaymentStatsFilterChange('current_week')}
              >
                Τρέχουσα Εβδομάδα
              </Button>
              <Button
                variant={paymentStatsFilter === 'all_time' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handlePaymentStatsFilterChange('all_time')}
              >
                Όλη η Περίοδος
              </Button>
            </Box>
            
            {/* History Filters */}
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Φίλτρα Ιστορικού
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Από"
                    type="date"
                    fullWidth
                    size="small"
                    value={historyDateFrom}
                    onChange={(e) => {
                      console.log('📅 Date FROM changed:', e.target.value);
                      setHistoryDateFrom(e.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Έως"
                    type="date"
                    fullWidth
                    size="small"
                    value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Ομαδοποίηση"
                    fullWidth
                    size="small"
                    value={historyGroupBy}
                    onChange={(e) => {
                      console.log('📊 GroupBy changed:', e.target.value);
                      setHistoryGroupBy(e.target.value);
                    }}
                  >
                    <MenuItem value="none">Καμία</MenuItem>
                    <MenuItem value="day">Ανά Ημέρα</MenuItem>
                    <MenuItem value="week">Ανά Εβδομάδα</MenuItem>
                    <MenuItem value="month">Ανά Μήνα</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              {(historyDateFrom || historyDateTo || historyGroupBy !== 'none') && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    onClick={() => {
                      setHistoryDateFrom('');
                      setHistoryDateTo('');
                      setHistoryGroupBy('none');
                    }}
                  >
                    Καθαρισμός Φίλτρων
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Statistics Cards */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Συνολικός Μισθός
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    €{paymentStats.totalSalary.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Πληρωμένα
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    €{paymentStats.totalPaid.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Υπόλοιπο
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                    €{paymentStats.unpaid.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider />

            {/* Payment History Table */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Ιστορικό Συναλλαγών
              </Typography>
              
              {paymentHistory.length === 0 ? (
                <Alert severity="info">
                  Δεν υπάρχουν καταχωρημένες πληρωμές για αυτόν τον υπάλληλο.
                </Alert>
              ) : (() => {
                const groupedHistory = getFilteredAndGroupedHistory();
                
                if (groupedHistory.length === 0) {
                  return (
                    <Alert severity="info">
                      Δεν βρέθηκαν πληρωμές με τα επιλεγμένα φίλτρα.
                    </Alert>
                  );
                }
                
                return (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {groupedHistory.map((group, groupIndex) => (
                    <Box key={group.groupKey || groupIndex}>
                      {/* Group Header (only if grouped) */}
                      {group.groupLabel && (
                        <Box sx={{ 
                          bgcolor: '#e3f2fd', 
                          p: 1.5, 
                          mb: 1, 
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {group.groupLabel}
                          </Typography>
                          <Chip 
                            label={`Σύνολο: €${group.totalAmount.toFixed(2)}`}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      )}
                      
                      {/* Payments in this group */}
                      {group.items.map((payment) => (
                    <Paper 
                      key={payment.id} 
                      variant="outlined" 
                      sx={{ p: 2, mb: 2, position: 'relative', overflow: 'visible' }}
                    >
                      <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 10 }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenEditPayment(payment)}
                          title="Επεξεργασία"
                          sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeletePayment(payment.id)}
                          title="Διαγραφή"
                          sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Ημερομηνία Πληρωμής
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {dayjs(payment.payment_date).format('DD/MM/YYYY')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Ποσό
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ fontWeight: 'bold', color: '#2e7d32' }}
                          >
                            €{parseFloat(payment.amount).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Μέθοδος Πληρωμής
                          </Typography>
                          <Chip 
                            label={
                              payment.payment_method === 'cash' ? 'Μετρητά' :
                              payment.payment_method === 'bank_transfer' ? 'Τράπεζα' :
                              payment.payment_method === 'check' ? 'Επιταγή' : payment.payment_method
                            }
                            size="small"
                            color={payment.payment_method === 'cash' ? 'success' : 'primary'}
                          />
                        </Grid>
                        {payment.reference && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Αναφορά
                            </Typography>
                            <Typography variant="body1">
                              {payment.reference}
                            </Typography>
                          </Grid>
                        )}
                        {payment.allocated_weeks && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                              Εβδομάδες που πληρώθηκαν
                            </Typography>
                            <Typography variant="body2">
                              {payment.allocated_weeks}
                            </Typography>
                          </Grid>
                        )}
                        {payment.notes && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                              Σημειώσεις
                            </Typography>
                            <Typography variant="body2">
                              {payment.notes}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                      ))}
                    </Box>
                  ))}
                </Box>
                );
              })()}
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenNewPayment}
          >
            Νέα Πληρωμή
          </Button>
          <Button onClick={closePaymentHistoryModal}>Κλείσιμο</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialog} onClose={handleCloseEditPayment} maxWidth="sm" fullWidth>
        <DialogTitle>
          Επεξεργασία Πληρωμής
          {editingPayment && (
            <Typography variant="body2" color="textSecondary">
              Πληρωμή #{editingPayment.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Ημερομηνία Πληρωμής"
                InputLabelProps={{ shrink: true }}
                value={editPaymentForm.payment_date}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Ποσό"
                value={editPaymentForm.amount}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                InputProps={{ startAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Τρόπος Πληρωμής</InputLabel>
                <Select
                  value={editPaymentForm.payment_method}
                  label="Τρόπος Πληρωμής"
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                  <MenuItem value="check">Επιταγή</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Αριθμός Αναφοράς"
                value={editPaymentForm.reference}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, reference: e.target.value })}
                placeholder="π.χ. αριθμός επιταγής"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Σημειώσεις"
                multiline
                rows={3}
                value={editPaymentForm.notes}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditPayment}>Ακύρωση</Button>
          <Button onClick={handleSaveEditPayment} variant="contained" color="primary">
            Αποθήκευση
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Payment Dialog */}
      <Dialog open={newPaymentDialog} onClose={handleCloseNewPayment} maxWidth="md" fullWidth>
        <DialogTitle>
          Νέα Πληρωμή
          {paymentHistoryModal.emp && (
            <Typography variant="body2" color="textSecondary">
              {paymentHistoryModal.emp.name} - {paymentHistoryModal.emp.position}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Ημερομηνία Πληρωμής"
                InputLabelProps={{ shrink: true }}
                value={newPaymentForm.payment_date}
                onChange={(e) => setNewPaymentForm({ ...newPaymentForm, payment_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Ποσό"
                value={newPaymentForm.amount}
                onChange={(e) => setNewPaymentForm({ ...newPaymentForm, amount: e.target.value })}
                InputProps={{ startAdornment: '€' }}
              />
            </Grid>
            
            {/* Unpaid Weeks Info (Read-only) */}
            {unpaidWeeks.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Η πληρωμή θα κατανεμηθεί αυτόματα στις παλαιότερες απλήρωτες εβδομάδες (FIFO)
                  </Typography>
                </Alert>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Απλήρωτες Εβδομάδες
                  <Chip 
                    label={`Σύνολο: €${unpaidWeeks.reduce((sum, w) => sum + w.unpaid, 0).toFixed(2)}`} 
                    size="small" 
                    color="error" 
                  />
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: '#f9f9f9' }}>
                  {unpaidWeeks.map((week, index) => {
                    // Calculate how much will be allocated to this week
                    const paymentAmount = parseFloat(newPaymentForm.amount) || 0;
                    const previousWeeksTotal = unpaidWeeks.slice(0, index).reduce((sum, w) => sum + w.unpaid, 0);
                    const remainingForThisWeek = Math.max(0, paymentAmount - previousWeeksTotal);
                    const willBeAllocated = Math.min(remainingForThisWeek, week.unpaid);
                    
                    return (
                      <Box 
                        key={week.week_start} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          p: 1, 
                          mb: 0.5,
                          bgcolor: willBeAllocated > 0 ? '#e8f5e9' : 'transparent',
                          borderRadius: 1,
                          border: willBeAllocated > 0 ? '1px solid #4caf50' : '1px solid transparent'
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: willBeAllocated > 0 ? 'bold' : 'normal' }}>
                            {index + 1}. {dayjs(week.week_start).format('DD/MM/YYYY')}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            Μισθός: €{week.salary?.toFixed(2) || '0.00'}
                          </Typography>
                          <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                            Πληρωμένα: €{week.paid?.toFixed(2) || '0.00'}
                          </Typography>
                          <Typography variant="caption" color="error.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                            Υπόλοιπο: €{week.unpaid.toFixed(2)}
                          </Typography>
                        </Box>
                        {willBeAllocated > 0 && (
                          <Chip 
                            label={`Θα πληρωθεί: €${willBeAllocated.toFixed(2)}`}
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                
                {/* Overpayment info */}
                {unpaidWeeks.length > 0 && (() => {
                  const totalUnpaid = unpaidWeeks.reduce((sum, w) => sum + w.unpaid, 0);
                  const paymentAmount = parseFloat(newPaymentForm.amount) || 0;
                  const overpayment = Math.max(0, paymentAmount - totalUnpaid);
                  
                  if (overpayment > 0) {
                    return (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Προκαταβολή:</strong> Τα επιπλέον €{overpayment.toFixed(2)} θα καταχωρηθούν ως προκαταβολή 
                          για την τρέχουσα/επόμενη εβδομάδα.
                        </Typography>
                      </Alert>
                    );
                  }
                  return null;
                })()}
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Τρόπος Πληρωμής</InputLabel>
                <Select
                  value={newPaymentForm.payment_method}
                  label="Τρόπος Πληρωμής"
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                  <MenuItem value="check">Επιταγή</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Αριθμός Αναφοράς"
                value={newPaymentForm.reference}
                onChange={(e) => setNewPaymentForm({ ...newPaymentForm, reference: e.target.value })}
                placeholder="π.χ. αριθμός επιταγής"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Σημειώσεις"
                multiline
                rows={3}
                value={newPaymentForm.notes}
                onChange={(e) => setNewPaymentForm({ ...newPaymentForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewPayment}>Ακύρωση</Button>
          <Button onClick={handleSaveNewPayment} variant="contained" color="primary">
            Αποθήκευση
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unified Employee Management Modal */}
      <EmployeeManagementModal
        open={unifiedModal.open} 
        employee={unifiedModal.emp}
        tab={unifiedModal.tab}
        onClose={() => {
          setUnifiedModal({ open: false, emp: null, tab: 0 });
          setEmployeeShifts([]);
          setCurrentEmployee(null);
        }}
        onTabChange={(newTab) => {
          setUnifiedModal({ ...unifiedModal, tab: newTab });
          // Fetch fixed schedule when switching to fixed schedule tab
          if (newTab === 3 && unifiedModal.emp) {
            fetchFixedSchedule(unifiedModal.emp.id);
          }
        }}
        positions={positions}
        presetHours={presetHours}
        editForm={editForm}
        onEditFormChange={handleEditChange}
        onEditFormSet={setEditForm}
        onEditSave={handleEditSave}
        onPreferencesSave={handlePreferencesSave}
        paymentStats={paymentStats}
        paymentHistory={paymentHistory}
        employeeShifts={employeeShiftsData || employeeShifts}
        onOpenNewPayment={handleOpenNewPayment}
        onOpenEditPayment={handleOpenEditPayment}
        onDeletePayment={handleDeletePayment}
        showFixedSchedule={true}
        fixedSchedule={fixedSchedule}
        onFixedScheduleChange={setFixedSchedule}
        onSaveFixedSchedule={handleSaveFixedSchedule}
        savingFixedSchedule={savingFixedSchedule}
        onRefetchPaymentHistory={refetchPaymentHistory}
        onRefetchShifts={refetchShifts}
      />

      <PlanLimitModal
        open={planLimitModal}
        onClose={() => setPlanLimitModal(false)}
        mode="employee_limit"
        maxActiveEmployees={maxActiveEmployees}
        currentCount={activeEmployeeCount}
        canStartTrial={canStartTrial}
        effectivePlan={effectivePlan}
        demoMode={demoMode}
      />

    </Box>
  );
}
